import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsDescriptionStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    wrapper?: InjectableCss;
    tableWrapper?: GridContainerProps;
    rowWrapper?: GridItemProps;
    label?: InjectableCss;
    value?: InjectableCss;
}

export const descriptionStyles: ProductDetailsDescriptionStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    wrapper: {
        css: css`
            margin: 30px 0;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin: 20px 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            h6 {
                margin-bottom: 5px;
                color: ${getColor("primary")};
                font-size: 16px;
                font-weight: 600;
            }
        `,
    },
    tableWrapper: {
        gap: 0,
        css: css`
            border: ${getColor("common.border")} 1px solid;
            div[class^="GridOffset"] {
                max-width: 100%;
            }
        `,
    },
    rowWrapper: {
        width: 12,
        css: css`
            margin: auto;
            border-bottom: ${getColor("common.border")} 1px solid;
            display: flex;
            &:nth-child(even) {
                background: #f7fafc;
            }
            &:last-child {
                border-bottom: none;
            }
        `,
    },
    label: {
        css: css`
            border-right: ${getColor("common.border")} 1.5px solid;
            width: 50%;
            padding: 5px 15px;
            font-weight: bold;
        `,
    },
    value: {
        css: css`
            width: 50%;
            padding: 5px 15px;
        `,
    },
};

const styles = descriptionStyles;

const ProductDetailsTable: React.FC<Props> = ({ product }) => {
    if (!product.detail) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productDescriptionSpinner" />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <GridContainer {...styles.tableWrapper}>
                <GridItem {...styles.rowWrapper}>
                    <StyledWrapper {...styles.label}>HESCO Part Number</StyledWrapper>
                    <StyledWrapper {...styles.value}>{product.productNumber}</StyledWrapper>
                </GridItem>
                {product.customerProductNumber && (
                    <GridItem {...styles.rowWrapper}>
                        <StyledWrapper {...styles.label}>My Part Number</StyledWrapper>
                        <StyledWrapper {...styles.value}>{product.customerProductNumber}</StyledWrapper>
                    </GridItem>
                )}
                {product.detail.upcCode && (
                    <GridItem {...styles.rowWrapper}>
                        <StyledWrapper {...styles.label}>UPC</StyledWrapper>
                        <StyledWrapper {...styles.value}>{product.detail.upcCode}</StyledWrapper>
                    </GridItem>
                )}
                {product.manufacturerItem && (
                    <GridItem {...styles.rowWrapper}>
                        <StyledWrapper {...styles.label}>Manufacturer Part Number</StyledWrapper>
                        <StyledWrapper {...styles.value}>{product.manufacturerItem}</StyledWrapper>
                    </GridItem>
                )}
                {product.brand && (
                    <GridItem {...styles.rowWrapper}>
                        <StyledWrapper {...styles.label}>Manufacturer</StyledWrapper>
                        <StyledWrapper {...styles.value}>{product.brand.name}</StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsTable),
    definition: {
        displayName: "Product Details Table",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
